import { Copy, RefreshCw, Send, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { CaseStatus } from '../../shared/case-schema.js';
import type { AdminCaseDetail, AdminReportRow } from '../admin-types.js';
import { adminApi } from '../api.js';
import { StatusBadge } from './StatusBadge.js';
import { Button, Field, Input, Loading, Notice, Textarea } from './ui.js';

export function AdminReportDrawer({
  row,
  close,
  reloadReports,
}: {
  row: AdminReportRow;
  close: () => void;
  reloadReports: () => Promise<void>;
}) {
  const [detail, setDetail] = useState<AdminCaseDetail | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [secureUrl, setSecureUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setDetail(await adminApi<AdminCaseDetail>(`/api/admin/reports/${row.report.reportId}/case`));
  }, [row.report.reportId]);
  useEffect(() => void load().catch((requestError) => setError(String(requestError))), [load]);
  useEffect(() => {
    if (!row.report.proof.photo) return;
    let active = true;
    let objectUrl: string | null = null;
    fetch(`/api/admin/reports/${row.report.reportId}/photo`, { credentials: 'same-origin' })
      .then((response) =>
        response.ok ? response.blob() : Promise.reject(new Error('Photo unavailable')),
      )
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (active) setPhotoUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [row.report.proof.photo, row.report.reportId]);

  const mutate = async <T,>(path: string, body: object): Promise<T | null> => {
    setError('');
    try {
      const value = await adminApi<T>(path, { method: 'POST', body: JSON.stringify(body) });
      await load();
      await reloadReports();
      return value;
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'The action failed');
      await load().catch(() => undefined);
      return null;
    }
  };
  const status = async (nextStatus: CaseStatus, terminal = false) => {
    if (!detail || (terminal && !window.confirm('Confirm this case update?'))) return;
    await mutate(`/api/admin/reports/${row.report.reportId}/case/status`, {
      expectedVersion: detail.caseRecord.version,
      nextStatus,
    });
  };
  const regenerate = async (role: 'GUEST' | 'VENUE') => {
    if (!detail) return;
    const value = await mutate<{ url: string }>(
      `/api/admin/reports/${row.report.reportId}/case/access`,
      { role, expectedVersion: detail.caseRecord.version },
    );
    if (value) setSecureUrl(value.url);
  };

  return (
    <div
      className="drawer-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <aside className="report-drawer admin-case-drawer" role="dialog" aria-modal="true">
        <button className="drawer-close" onClick={close} aria-label="Close">
          <X />
        </button>
        {!detail ? (
          <Loading label="Loading return case..." />
        ) : (
          <>
            <div className="admin-case-heading">
              <span className="report-reference">
                {detail.report.reportId.slice(0, 8).toUpperCase()}
              </span>
              <StatusBadge status={detail.caseRecord.status} />
            </div>
            <h2>{detail.report.item.description}</h2>
            {error ? <Notice tone="danger">{error}</Notice> : null}
            {secureUrl ? (
              <Notice tone="success">
                New secure link ready. It was also sent when the recipient email was configured.
                <Button
                  variant="secondary"
                  onClick={() => void navigator.clipboard.writeText(secureUrl)}
                >
                  <Copy size={15} /> Copy link
                </Button>
              </Notice>
            ) : null}

            <dl>
              <dt>Venue</dt>
              <dd>{detail.report.venueContext.venueName}</dd>
              <dt>Case status</dt>
              <dd>
                {detail.caseRecord.status} · version {detail.caseRecord.version}
              </dd>
              <dt>Category / color</dt>
              <dd>
                {detail.report.item.category} · {detail.report.item.color}
              </dd>
              <dt>Contact</dt>
              <dd>
                {detail.report.contact.firstName} {detail.report.contact.lastName}
                <br />
                {detail.report.contact.email}
              </dd>
              {detail.caseRecord.shipping ? (
                <>
                  <dt>Shipping address</dt>
                  <dd className="pre-wrap">
                    {detail.caseRecord.shipping.recipientName}
                    <br />
                    {detail.caseRecord.shipping.address}
                  </dd>
                </>
              ) : null}
              {detail.caseRecord.shipment ? (
                <>
                  <dt>Shipment</dt>
                  <dd>
                    {detail.caseRecord.shipment.carrier} ·{' '}
                    {detail.caseRecord.shipment.trackingNumber}
                  </dd>
                </>
              ) : null}
            </dl>
            {photoUrl ? (
              <img className="protected-photo" src={photoUrl} alt="Private proof" />
            ) : null}

            <section className="drawer-section">
              <h3>Case actions</h3>
              <div className="venue-action-buttons">
                {detail.caseRecord.status === 'SUBMITTED' ? (
                  <Button variant="secondary" onClick={() => void status('REVIEWING')}>
                    Start review
                  </Button>
                ) : null}
                {['SUBMITTED', 'REVIEWING'].includes(detail.caseRecord.status) ? (
                  <Button onClick={() => void status('POSSIBLE_MATCH')}>Possible match</Button>
                ) : null}
                {['SUBMITTED', 'REVIEWING', 'POSSIBLE_MATCH'].includes(detail.caseRecord.status) ? (
                  <Button variant="danger" onClick={() => void status('CLOSED_NOT_FOUND', true)}>
                    Close as not found
                  </Button>
                ) : null}
                {['SHIPPED', 'READY_FOR_PICKUP'].includes(detail.caseRecord.status) ? (
                  <Button onClick={() => void status('RETURNED', true)}>Mark returned</Button>
                ) : null}
              </div>
              {detail.caseRecord.status === 'RETURN_DETAILS_PROVIDED' ? (
                <AdminFulfillment detail={detail} mutate={mutate} />
              ) : null}
              {['RETURNED', 'CLOSED_NOT_FOUND'].includes(detail.caseRecord.status) ? (
                <ReopenForm detail={detail} mutate={mutate} />
              ) : null}
            </section>

            <section className="drawer-section">
              <h3>Secure access</h3>
              <div className="venue-action-buttons">
                <Button variant="secondary" onClick={() => void regenerate('GUEST')}>
                  <RefreshCw size={15} /> Regenerate guest link
                </Button>
                <Button variant="secondary" onClick={() => void regenerate('VENUE')}>
                  <RefreshCw size={15} /> Regenerate venue link
                </Button>
              </div>
            </section>

            <section className="drawer-section">
              <h3>Notifications</h3>
              <div className="notification-list">
                {detail.notifications.map((notification) => (
                  <article key={notification.id}>
                    <span>
                      <strong>{notification.category.replaceAll('_', ' ')}</strong>
                      <small>
                        {notification.maskedRecipient} · {notification.state}
                        {notification.failureCode ? ` · ${notification.failureCode}` : ''}
                      </small>
                    </span>
                    {notification.state === 'FAILED' ? (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          void mutate(`/api/admin/notifications/${notification.id}/retry`, {})
                        }
                      >
                        <Send size={14} /> Retry
                      </Button>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>

            <section className="drawer-section">
              <h3>Case history</h3>
              <div className="admin-history">
                {[...detail.caseRecord.history].reverse().map((entry) => (
                  <article key={entry.id}>
                    <strong>{entry.publicMessage ?? entry.action.replaceAll('_', ' ')}</strong>
                    <small>
                      {entry.actor} · {new Date(entry.at).toLocaleString('en-GB')}
                    </small>
                    {entry.privateNote ? <p>{entry.privateNote}</p> : null}
                  </article>
                ))}
              </div>
            </section>

            <section className="drawer-section">
              <h3>Redacted transcript</h3>
              <div className="admin-transcript">
                {detail.report.intake.redactedTranscript.map((entry, index) => (
                  <p key={index}>
                    <strong>{entry.role}:</strong> {entry.text}
                  </p>
                ))}
              </div>
            </section>
          </>
        )}
      </aside>
    </div>
  );
}

function AdminFulfillment({
  detail,
  mutate,
}: {
  detail: AdminCaseDetail;
  mutate: <T>(path: string, body: object) => Promise<T | null>;
}) {
  const shipping = detail.caseRecord.returnMethod === 'SHIP';
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [pickupInstructions, setPickupInstructions] = useState('');
  return (
    <div className="drawer-form">
      {shipping ? (
        <>
          <Field label="Carrier">
            <Input value={carrier} onChange={(event) => setCarrier(event.target.value)} />
          </Field>
          <Field label="Tracking number">
            <Input
              value={trackingNumber}
              onChange={(event) => setTrackingNumber(event.target.value)}
            />
          </Field>
        </>
      ) : (
        <Field label="Pickup instructions">
          <Textarea
            value={pickupInstructions}
            onChange={(event) => setPickupInstructions(event.target.value)}
          />
        </Field>
      )}
      <Button
        disabled={shipping ? !carrier.trim() || !trackingNumber.trim() : !pickupInstructions.trim()}
        onClick={() =>
          void mutate(
            `/api/admin/reports/${detail.report.reportId}/case/fulfillment`,
            shipping
              ? {
                  expectedVersion: detail.caseRecord.version,
                  action: 'SHIP',
                  carrier,
                  trackingNumber,
                }
              : {
                  expectedVersion: detail.caseRecord.version,
                  action: 'PICKUP',
                  pickupInstructions,
                },
          )
        }
      >
        Save fulfillment
      </Button>
    </div>
  );
}

function ReopenForm({
  detail,
  mutate,
}: {
  detail: AdminCaseDetail;
  mutate: <T>(path: string, body: object) => Promise<T | null>;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="drawer-form">
      <Field label="Reason for reopening">
        <Textarea value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
      <Button
        variant="secondary"
        disabled={reason.trim().length < 3}
        onClick={() =>
          void mutate(`/api/admin/reports/${detail.report.reportId}/case/reopen`, {
            expectedVersion: detail.caseRecord.version,
            reason,
          })
        }
      >
        Reopen case
      </Button>
    </div>
  );
}
